import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiLayout, 
  FiShare2, 
  FiBarChart2, 
  FiBook, 
  FiMessageSquare, 
  FiArrowRight, 
  FiCheckCircle,
  FiActivity,
  FiLayers,
  FiCpu,
  FiUsers,
  FiTarget
} = FiIcons;

const LandingPage = () => {
  const scrollToFeatures = (e) => {
    e.preventDefault();
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#eaeef7] font-sans text-[#15191a] selection:bg-[#87ff8f] selection:text-[#152627]">
      
      {/* Navigation */}
      <nav className="fixed w-full bg-[#eaeef7]/90 backdrop-blur-md z-50 border-b border-[#152627]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className="bg-[#152627] p-2 rounded-lg">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-[#87ff8f]" />
              </div>
              <span className="text-xl font-bold tracking-tight text-[#15191a]">
                Org<span className="text-[#152627]">Dev</span> Platform
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link 
                to="/" 
                className="group px-6 py-2.5 rounded-full bg-[#152627] text-white text-sm font-bold hover:bg-[#15191a] transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                aria-label="Zur Anmeldung"
              >
                Anmelden 
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-[#87ff8f] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#152627]">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#87ff8f] rounded-full mix-blend-overlay filter blur-[120px] opacity-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#eaeef7] rounded-full mix-blend-overlay filter blur-[100px] opacity-5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="mb-12 lg:mb-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#87ff8f]/10 border border-[#87ff8f]/20 text-[#87ff8f] text-sm font-medium mb-8">
                <span className="flex h-2 w-2 rounded-full bg-[#87ff8f] animate-pulse"></span>
                Systemische Organisationsentwicklung
              </div>
              <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight text-white mb-6 leading-[1.1]">
                Verstehen Sie die <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#87ff8f] to-emerald-200">
                  Dynamik Ihres Teams
                </span>
              </h1>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
                Erkennen Sie verborgene Zusammenhänge durch Netzwerkanalysen, nutzen Sie datenbasierte Evaluationen und fördern Sie eine offene Feedback-Kultur für nachhaltiges Wachstum.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/" 
                  className="px-8 py-4 rounded-full bg-[#87ff8f] text-[#152627] text-lg font-bold hover:bg-white transition-all shadow-lg shadow-[#87ff8f]/20 hover:shadow-[#87ff8f]/40 text-center"
                >
                  Jetzt Starten
                </Link>
                <button 
                  onClick={scrollToFeatures}
                  className="px-8 py-4 rounded-full border border-gray-600 text-white text-lg font-medium hover:bg-white/5 transition-all text-center"
                >
                  Mehr erfahren
                </button>
              </div>
            </div>
            
            {/* Hero Visual / Abstract UI Representation */}
            <div className="relative rounded-2xl bg-[#15191a] border border-gray-800 p-4 shadow-2xl transform rotate-1 hover:rotate-0 transition-all duration-500">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#87ff8f] to-[#152627] rounded-2xl blur opacity-20"></div>
              <div className="relative bg-[#15191a] rounded-xl overflow-hidden aspect-video flex items-center justify-center border border-gray-800">
                 <div className="grid grid-cols-3 gap-8 p-8 w-full h-full opacity-80">
                    <div className="col-span-2 bg-[#152627] rounded-lg h-32 animate-pulse"></div>
                    <div className="bg-[#152627] rounded-lg h-32"></div>
                    <div className="bg-[#152627] rounded-lg h-20"></div>
                    <div className="col-span-2 bg-[#152627] rounded-lg h-20"></div>
                 </div>
                 {/* Floating Badge */}
                 <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#eaeef7] p-4 rounded-xl shadow-xl flex items-center gap-3">
                    <div className="bg-[#152627] p-2 rounded-full">
                      <SafeIcon icon={FiShare2} className="text-[#87ff8f]" />
                    </div>
                    <div>
                      <div className="text-[#152627] font-bold text-sm">System-Analyse</div>
                      <div className="text-xs text-gray-500">Verbindungen erkannt</div>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="py-24 bg-[#eaeef7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-[#15191a] mb-6">
              Ganzheitliche Werkzeuge für moderne Teams
            </h2>
            <p className="text-gray-600 text-lg">
              Unsere Plattform verbindet harte Daten mit weichen Faktoren, um ein vollständiges Bild Ihrer Organisation zu zeichnen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1: Network */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiShare2} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Vernetztes Denken</h3>
              <p className="text-gray-600 leading-relaxed">
                Visualisieren Sie komplexe Abhängigkeiten zwischen verschiedenen Organisationselementen in einem interaktiven Netzwerk-Diagramm.
              </p>
            </article>

            {/* Feature 2: Evaluation */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiBarChart2} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Präzise Evaluation</h3>
              <p className="text-gray-600 leading-relaxed">
                Nutzen Sie wissenschaftlich fundierte Fragebögen und Radar-Charts, um den Reifegrad Ihrer Teams in verschiedenen Clustern zu messen.
              </p>
            </article>

            {/* Feature 3: Handbook */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiBook} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Aktives Handbuch</h3>
              <p className="text-gray-600 leading-relaxed">
                Erhalten Sie automatisch kuratierte Handlungsempfehlungen basierend auf den Identifizierten Schwachstellen Ihres Teams.
              </p>
            </article>

            {/* Feature 4: Feedback */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiMessageSquare} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Team Feedback</h3>
              <p className="text-gray-600 leading-relaxed">
                Stärken Sie das Vertrauen durch anonymisiertes, konstruktives Peer-to-Peer Feedback und offene Kommunikationskanäle.
              </p>
            </article>

            {/* Feature 5: Structure */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiLayers} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Cluster & Struktur</h3>
              <p className="text-gray-600 leading-relaxed">
                Organisieren Sie Ihre Themen in logische Cluster und behalten Sie den Überblick über Verantwortung und Fortschritt.
              </p>
            </article>

            {/* Feature 6: Admin */}
            <article className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:border-[#152627]/20 transition-all group">
              <div className="w-14 h-14 rounded-xl bg-[#152627] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiCpu} className="w-7 h-7 text-[#87ff8f]" />
              </div>
              <h3 className="text-xl font-bold text-[#15191a] mb-3">Volle Kontrolle</h3>
              <p className="text-gray-600 leading-relaxed">
                Umfangreiche Admin-Tools zur Verwaltung von Teams, Nutzern und Inhalten ermöglichen eine flexible Anpassung.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-24 bg-[#152627] text-[#eaeef7] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
               <h2 className="text-3xl font-bold text-white mb-6">
                 Vom Datensatz zur Erkenntnis
               </h2>
               <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                 Oft bleiben die wahren Ursachen für Ineffizienz verborgen. Unsere "Causal Loop Diagrams" (CLD) machen diese unsichtbaren Wirkungsmechanismen sichtbar. Verstehen Sie nicht nur, <em>dass</em> ein Problem existiert, sondern <em>warum</em>.
               </p>
               <ul className="space-y-4">
                 {[
                   'Systemische Zusammenhänge erkennen',
                   'Hebelwirkung identifizieren',
                   'Nachhaltige Veränderungen anstoßen',
                   'Team-Dynamik visualisieren'
                 ].map((item, i) => (
                   <li key={i} className="flex items-center gap-3">
                     <div className="bg-[#87ff8f]/20 p-1 rounded-full">
                       <SafeIcon icon={FiCheckCircle} className="text-[#87ff8f] w-4 h-4" />
                     </div>
                     <span className="text-gray-200">{item}</span>
                   </li>
                 ))}
               </ul>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-[#15191a] p-8 rounded-2xl border border-gray-700 shadow-2xl">
                 <div className="flex items-center justify-between mb-8 border-b border-gray-700 pb-4">
                    <span className="text-[#87ff8f] font-mono text-sm">network_analysis.js</span>
                    <div className="flex gap-2">
                       <div className="w-3 h-3 rounded-full bg-red-500"></div>
                       <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                       <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                 </div>
                 {/* Abstract Network Visualization */}
                 <div className="relative h-64 w-full flex items-center justify-center">
                    <div className="absolute w-32 h-32 border-2 border-[#87ff8f]/30 rounded-full animate-[spin_10s_linear_infinite]"></div>
                    <div className="absolute w-48 h-48 border border-[#87ff8f]/10 rounded-full"></div>
                    
                    <div className="absolute top-1/4 left-1/4 bg-[#87ff8f] w-3 h-3 rounded-full shadow-[0_0_10px_#87ff8f]"></div>
                    <div className="absolute bottom-1/3 right-1/4 bg-[#152627] border border-[#87ff8f] w-4 h-4 rounded-full"></div>
                    <div className="absolute bottom-1/4 left-1/2 bg-white w-2 h-2 rounded-full"></div>
                    
                    {/* SVG Connecting Lines */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                       <line x1="25%" y1="25%" x2="50%" y2="75%" stroke="#87ff8f" strokeWidth="1" strokeOpacity="0.5" />
                       <line x1="50%" y1="75%" x2="75%" y2="66%" stroke="#87ff8f" strokeWidth="1" strokeOpacity="0.5" />
                       <line x1="75%" y1="66%" x2="25%" y2="25%" stroke="#87ff8f" strokeWidth="1" strokeOpacity="0.2" />
                    </svg>
                 </div>
                 <div className="mt-4 text-center">
                    <span className="text-xs text-gray-500 font-mono">Interaktive Cluster-Analyse</span>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#eaeef7]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#15191a] mb-6">Bereit für den Wandel?</h2>
          <p className="text-xl text-gray-600 mb-10">
            Starten Sie jetzt und bringen Sie Klarheit in Ihre Organisationsstruktur.
          </p>
          <div className="flex justify-center gap-4">
             <Link 
              to="/" 
              className="inline-flex items-center px-8 py-4 rounded-full bg-[#152627] text-white text-lg font-bold hover:bg-[#15191a] hover:scale-105 transition-all shadow-xl"
            >
              <SafeIcon icon={FiUsers} className="mr-2 text-[#87ff8f]" /> 
              Kostenlos registrieren
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#15191a] text-gray-400 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
             <div className="bg-[#152627] p-1.5 rounded border border-gray-700">
                <SafeIcon icon={FiActivity} className="w-5 h-5 text-[#87ff8f]" />
             </div>
             <span className="text-gray-200 font-semibold tracking-wide">OrgDev Platform</span>
          </div>
          
          <div className="flex gap-8 text-sm">
            <Link to="/" className="hover:text-[#87ff8f] transition-colors">Login</Link>
            <Link to="/" className="hover:text-[#87ff8f] transition-colors">Impressum</Link>
            <Link to="/" className="hover:text-[#87ff8f] transition-colors">Datenschutz</Link>
          </div>
          
          <p className="text-xs text-gray-600">
            &copy; {new Date().getFullYear()} OrgDev Platform. All Rights Reserved.
          </p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;